/**
 * Quiz Engine for Shopify
 * Handles multiple quiz types with JSON configuration
 */

class QuizEngine {
  constructor(config, formElement) {
    this.config = config;
    this.form = formElement;
    this.currentQuestionIndex = 0;
    this.answers = {};
    this.validationErrors = [];
    this.imageUploading = false;
    
    this.init();
  }

  init() {
    this.form.innerHTML = '';
    this.form.className = 'quiz-form';
    
    this.createHeader();
    this.createProgressBar();
    this.renderQuestions();
    this.createNavigationButtons();
    this.attachEventListeners();
    
    // Show first question
    this.showQuestion(0);

    document.body.classList.add('quiz-active');
  }

  createHeader() {
    const headerHtml = `
      <div class="quiz-header">
        <div class="quiz-header-container">
          <div class="quiz-back-button hidden" id="quiz-back-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </div>
          <a href="/" class="quiz-logo">
            <img src="">
          </a>
          <div class="quiz-close-button" id="quiz-close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </div>
        </div>
      </div>
    `;
    
    const headerContainer = document.createElement('div');
    headerContainer.innerHTML = headerHtml;
    this.form.parentNode.insertBefore(headerContainer.firstElementChild, this.form);
  }

  createProgressBar() {
    const progressHtml = `
      <div class="quiz-progress-wrapper">
        <div class="quiz-progress-bar">
          <div class="quiz-progress-fill" style="width: ${100 / this.config.questions.length}%"></div>
        </div>
        <div class="quiz-progress-text">
          Question <span id="current-question">1</span> of ${this.config.questions.length}
        </div>
      </div>
    `;
    
    const progressContainer = document.createElement('div');
    progressContainer.innerHTML = progressHtml;
    this.form.appendChild(progressContainer.firstElementChild);
  }

  renderQuestions() {
    const questionsContainer = document.createElement('div');
    questionsContainer.className = 'quiz-questions-container';
    
    this.config.questions.forEach((question, index) => {
      const questionEl = this.createQuestionElement(question, index);
      questionsContainer.appendChild(questionEl);
    });
    
    this.form.appendChild(questionsContainer);
  }

  createQuestionElement(question, index) {
    const questionEl = document.createElement('div');
    questionEl.className = `quiz-question ${index === 0 ? 'active' : 'hidden'}`;
    questionEl.dataset.questionId = question.id;
    questionEl.dataset.questionIndex = index;
    
    let questionHtml = `
      <div class="quiz-question-content">
        <h2 class="quiz-question-title">${question.title}</h2>
    `;
    
    if (question.description) {
      questionHtml += `<p class="quiz-question-description">${question.description}</p>`;
    }
    
    // Render different question types
    switch (question.type) {
      case 'radio':
        questionHtml += this.renderRadioQuestion(question);
        break;
      case 'checkbox':
        questionHtml += this.renderCheckboxQuestion(question);
        break;
      case 'image-radio':
        questionHtml += this.renderImageRadioQuestion(question);
        break;
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
        questionHtml += this.renderInputQuestion(question);
        break;
      case 'textarea':
        questionHtml += this.renderTextareaQuestion(question);
        break;
      case 'file':
        questionHtml += this.renderFileQuestion(question);
        break;
      case 'form':
        questionHtml += this.renderFormQuestion(question);
        break;
    }
    
    questionHtml += '</div>';
    questionEl.innerHTML = questionHtml;
    
    return questionEl;
  }

  renderRadioQuestion(question) {
    const layout = question.layout || 'vertical';
    let html = `<div class="quiz-options-container ${layout}">`;
    
    question.options.forEach((option, idx) => {
      const optionId = `${question.id}-${idx}`;
      html += `
        <div class="quiz-option">
          <input type="radio" 
                 id="${optionId}" 
                 name="${question.id}" 
                 value="${option.value}"
                 ${question.required ? 'required' : ''}>
          <label for="${optionId}" class="${option.highlighted ? 'highlighted' : ''}">
            ${option.label}
            ${option.hasTextInput ? `<input type="text" class="quiz-other-input hidden" id="${question.id}-other" placeholder="Please specify...">` : ''}
          </label>
        </div>
      `;
    });
    
    html += '</div>';
    return html;
  }

  renderCheckboxQuestion(question) {
    const layout = question.layout || 'vertical';
    let html = `<div class="quiz-options-container ${layout}">`;
    
    question.options.forEach((option, idx) => {
      const optionId = `${question.id}-${idx}`;
      html += `
        <div class="quiz-option">
          <input type="checkbox" 
                 id="${optionId}" 
                 name="${question.id}" 
                 value="${option.value}">
          <label for="${optionId}">
            ${option.label}
          </label>
        </div>
      `;
    });
    
    html += '</div>';
    return html;
  }

  renderImageRadioQuestion(question) {
    const layout = question.layout || 'grid-3';
    let html = `<div class="quiz-image-options-container ${layout}">`;
    
    question.options.forEach((option, idx) => {
      const optionId = `${question.id}-${idx}`;
      html += `
        <div class="quiz-image-option">
          <input type="radio" 
                 id="${optionId}" 
                 name="${question.id}" 
                 value="${option.value}"
                 ${question.required ? 'required' : ''}>
          <label for="${optionId}">
            ${option.image ? `<img src="${option.image}" alt="${option.label}">` : ''}
            <span>${option.label}</span>
          </label>
        </div>
      `;
    });
    
    html += '</div>';
    return html;
  }

  renderInputQuestion(question) {
    return `
      <div class="quiz-input-container">
        <input type="${question.type}" 
               id="${question.id}" 
               name="${question.id}"
               placeholder="${question.placeholder || ''}"
               ${question.required ? 'required' : ''}
               ${question.pattern ? `pattern="${question.pattern}"` : ''}
               class="quiz-input">
      </div>
    `;
  }

  renderTextareaQuestion(question) {
    return `
      <div class="quiz-textarea-container">
        <textarea id="${question.id}" 
                  name="${question.id}"
                  placeholder="${question.placeholder || ''}"
                  rows="${question.rows || 4}"
                  ${question.required ? 'required' : ''}
                  class="quiz-textarea"></textarea>
      </div>
    `;
  }

  renderFileQuestion(question) {
    return `
      <div class="quiz-file-container">
        <div class="quiz-file-upload">
          <input type="file" 
                 id="${question.id}" 
                 name="${question.id}"
                 accept="${question.accept || 'image/*'}"
                 ${question.required ? 'required' : ''}
                 class="quiz-file-input hidden">
          <button type="button" class="quiz-file-button" onclick="document.getElementById('${question.id}').click()">
            ${question.buttonText || 'SELECT PHOTO'}
          </button>
          <div class="quiz-file-preview hidden" id="${question.id}-preview"></div>
        </div>
        ${question.skipOption ? `
          <div class="quiz-skip-option">
            <a href="#" class="quiz-skip-link" data-skip="${question.id}">
              ${question.skipText || 'If not, skip this part.'}
            </a>
          </div>
        ` : ''}
      </div>
    `;
  }

  renderFormQuestion(question) {
    let html = '<div class="quiz-form-container">';
    
    if (question.errorContainer) {
      html += '<div class="quiz-form-errors" id="form-errors"></div>';
    }
    
    question.fields.forEach(field => {
      html += `
        <div class="quiz-form-field">
          <input type="${field.type}" 
                 id="${field.id}" 
                 name="${field.name}"
                 placeholder="${field.placeholder}"
                 ${field.required ? 'required' : ''}
                 ${field.pattern ? `pattern="${field.pattern}"` : ''}
                 class="quiz-form-input">
        </div>
      `;
    });
    
    if (question.disclaimer) {
      html += `<div class="quiz-form-disclaimer">${question.disclaimer}</div>`;
    }
    
    if (question.submitButton) {
      html += `
        <button type="button" class="quiz-submit-button" id="quiz-final-submit">
          ${question.submitText || 'Submit'}
        </button>
      `;
    }
    
    html += '</div>';
    return html;
  }

  createNavigationButtons() {
    const navHtml = `
    <div class="quiz-navigation" id="quiz-navigation">
    <button type="button" class="quiz-nav-button quiz-prev" id="quiz-prev-btn">

        Previous
    </button>

    <button type="button" class="quiz-nav-button quiz-next" id="quiz-next-btn">
        Next
    </button>
    </div>
    `;
    
    const navContainer = document.createElement('div');
    navContainer.innerHTML = navHtml;
    document.body.appendChild(navContainer.firstElementChild);
  }

  attachEventListeners() {
    // Navigation
    document.getElementById('quiz-next-btn')?.addEventListener('click', () => this.nextQuestion());
    document.getElementById('quiz-prev-btn')?.addEventListener('click', () => this.previousQuestion());
    document.getElementById('quiz-back-btn')?.addEventListener('click', () => this.previousQuestion());
    
    // Close button
    document.getElementById('quiz-close-btn')?.addEventListener('click', () => this.showExitConfirmation());
    
    // Auto-advance for radio buttons (if configured)
    this.form.addEventListener('change', (e) => {
      if (e.target.type === 'radio') {
        const question = this.config.questions[this.currentQuestionIndex];
        if (question.autoAdvance !== false) {
          // Check if this option has a text input
          const option = question.options.find(opt => opt.value === e.target.value);
          if (!option?.hasTextInput) {
            setTimeout(() => this.nextQuestion(), 300);
          } else {
            // Show the text input
            const otherInput = e.target.closest('.quiz-question').querySelector('.quiz-other-input');
            if (otherInput) {
              otherInput.classList.remove('hidden');
              otherInput.focus();
            }
          }
        }
      }
    });
    
    // File upload handling
    this.form.addEventListener('change', (e) => {
      if (e.target.type === 'file') {
        this.handleFileUpload(e.target);
      }
    });
    
    // Skip links
    this.form.addEventListener('click', (e) => {
      if (e.target.classList.contains('quiz-skip-link')) {
        e.preventDefault();
        this.nextQuestion();
      }
    });
    
    // Final submit
    document.addEventListener('click', (e) => {
      if (e.target.id === 'quiz-final-submit') {
        this.submitQuiz();
      }
    });
  }

  showQuestion(index) {
    // Hide all questions
    this.form.querySelectorAll('.quiz-question').forEach(q => {
      q.classList.remove('active');
      q.classList.add('hidden');
    });
    
    // Show current question
    const currentQuestion = this.form.querySelector(`[data-question-index="${index}"]`);
    if (currentQuestion) {
      currentQuestion.classList.remove('hidden');
      currentQuestion.classList.add('active');
    }
    
    // Update progress
    this.updateProgress(index);
    
    // Update navigation
    this.updateNavigation(index);
    
    // Call onShow callback if exists
    const question = this.config.questions[index];
    if (question.onShow) {
      question.onShow(this);
    }
  }

  updateProgress(index) {
    const progress = ((index + 1) / this.config.questions.length) * 100;
    const progressFill = document.querySelector('.quiz-progress-fill');
    if (progressFill) {
      progressFill.style.width = `${progress}%`;
    }
    
    const currentQuestionSpan = document.getElementById('current-question');
    if (currentQuestionSpan) {
      currentQuestionSpan.textContent = index + 1;
    }
  }

  updateNavigation(index) {
    const prevBtn = document.getElementById('quiz-prev-btn');
    const nextBtn = document.getElementById('quiz-next-btn');
    const backBtn = document.getElementById('quiz-back-btn');
    const navigation = document.getElementById('quiz-navigation');
    
    // Show/hide previous button
    if (prevBtn) {
      prevBtn.classList.toggle('hidden', index === 0);
    }
    if (backBtn) {
      backBtn.classList.toggle('hidden', index === 0);
    }
    
    // Update next button text
    if (nextBtn) {
      const question = this.config.questions[index];
      if (question.type === 'form' && question.submitButton) {
        navigation.classList.add('hidden');
      } else {
        navigation.classList.remove('hidden');
        nextBtn.textContent = index === this.config.questions.length - 1 ? 'Submit' : 'Next';
      }
    }
  }

  nextQuestion() {
    if (!this.validateCurrentQuestion()) {
      return;
    }
    
    this.saveCurrentAnswer();
    
    const question = this.config.questions[this.currentQuestionIndex];
    
    // Check for disqualification
    if (this.checkDisqualification(question)) {
      this.showDisqualificationMessage();
      return;
    }
    
    if (this.currentQuestionIndex < this.config.questions.length - 1) {
      this.currentQuestionIndex++;
      this.showQuestion(this.currentQuestionIndex);
    } else {
      this.submitQuiz();
    }
  }

  previousQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
      this.showQuestion(this.currentQuestionIndex);
    }
  }

  validateCurrentQuestion() {
    const question = this.config.questions[this.currentQuestionIndex];
    const questionEl = this.form.querySelector(`[data-question-id="${question.id}"]`);
    
    // Clear previous errors
    questionEl.classList.remove('invalid');
    this.validationErrors = [];
    
    if (!question.required && question.type !== 'form') {
      return true;
    }
    
    let isValid = true;
    
    switch (question.type) {
      case 'radio':
      case 'image-radio':
        const radioChecked = questionEl.querySelector(`input[name="${question.id}"]:checked`);
        if (question.required && !radioChecked) {
          isValid = false;
          this.validationErrors.push(question.errorMessage || 'Please select an option');
        }
        // Check for other input if needed
        if (radioChecked) {
          const option = question.options.find(opt => opt.value === radioChecked.value);
          if (option?.hasTextInput) {
            const otherInput = questionEl.querySelector('.quiz-other-input');
            if (otherInput && !otherInput.value.trim()) {
              isValid = false;
              this.validationErrors.push('Please specify');
            }
          }
        }
        break;
        
      case 'checkbox':
        const checkboxes = questionEl.querySelectorAll(`input[name="${question.id}"]:checked`);
        if (question.validation) {
          if (question.validation.minSelections && checkboxes.length < question.validation.minSelections) {
            isValid = false;
            this.validationErrors.push(`Please select at least ${question.validation.minSelections} options`);
          }
          if (question.validation.maxSelections && checkboxes.length > question.validation.maxSelections) {
            isValid = false;
            this.validationErrors.push(`Please select no more than ${question.validation.maxSelections} options`);
          }
        }
        break;
        
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'textarea':
        const input = questionEl.querySelector(`#${question.id}`);
        if (question.required && !input.value.trim()) {
          isValid = false;
          this.validationErrors.push(question.errorMessage || 'This field is required');
        }
        if (question.validation && input.value) {
          if (question.validation.pattern) {
            const regex = new RegExp(question.validation.pattern);
            if (!regex.test(input.value)) {
              isValid = false;
              this.validationErrors.push(question.validation.errorMessage || 'Invalid format');
            }
          }
        }
        break;
        
      case 'form':
        isValid = this.validateFormQuestion(question, questionEl);
        break;
    }
    
    if (!isValid) {
      questionEl.classList.add('invalid');
      this.showValidationErrors();
    }
    
    return isValid;
  }

validateFormQuestion(question, questionEl) {
    let isValid = true;
    const errors = [];
    
    question.fields.forEach(field => {
      const input = questionEl.querySelector(`#${field.id}`);
      if (!input) return;
      
      input.classList.remove('invalid');
      
      // Required check
      if (field.required && !input.value.trim()) {
        isValid = false;
        input.classList.add('invalid');
        errors.push(field.errorMessage || `${field.placeholder} is required`);
        return;
      }
      
      // Skip pattern validation for email and tel types 
      if (field.pattern && input.value && field.type !== 'email' && field.type !== 'tel') {
        const regex = new RegExp(field.pattern);
        if (!regex.test(input.value)) {
          isValid = false;
          input.classList.add('invalid');
          errors.push(field.validationMessage || `Invalid ${field.placeholder.toLowerCase()}`);
        }
      }
      
      // Email validation
      if (field.type === 'email' && input.value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(input.value)) {
          isValid = false;
          input.classList.add('invalid');
          errors.push('Please enter a valid email address');
        }
      }
      
      // Phone validation
      if (field.type === 'tel' && input.value) {
        const phoneRegex = /^[\d\s\-\(\)]+$/;
        if (!phoneRegex.test(input.value) || input.value.replace(/\D/g, '').length !== 10) {
          isValid = false;
          input.classList.add('invalid');
          errors.push(field.validationMessage || 'Please enter a valid 10-digit phone number');
        }
      }
    });
    
    // Show errors in form error container
    const errorContainer = questionEl.querySelector('#form-errors');
    if (errorContainer) {
      errorContainer.innerHTML = errors.map(err => `<p>${err}</p>`).join('');
    }
    
    this.validationErrors = errors;
    
    return isValid;
  }

  showValidationErrors() {
    // You can customize how errors are displayed
    console.log('Validation errors:', this.validationErrors);
  }

  saveCurrentAnswer() {
    const question = this.config.questions[this.currentQuestionIndex];
    const questionEl = this.form.querySelector(`[data-question-id="${question.id}"]`);
    
    switch (question.type) {
      case 'radio':
      case 'image-radio':
        const radioValue = questionEl.querySelector(`input[name="${question.id}"]:checked`)?.value;
        this.answers[question.id] = radioValue;
        
        // Check for other input
        const option = question.options.find(opt => opt.value === radioValue);
        if (option?.hasTextInput) {
          const otherInput = questionEl.querySelector('.quiz-other-input');
          if (otherInput) {
            this.answers[`${question.id}_other`] = otherInput.value;
          }
        }
        break;
        
      case 'checkbox':
        const checkboxValues = Array.from(questionEl.querySelectorAll(`input[name="${question.id}"]:checked`))
          .map(cb => cb.value);
        this.answers[question.id] = checkboxValues;
        break;
        
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'textarea':
        const inputValue = questionEl.querySelector(`#${question.id}`)?.value;
        this.answers[question.id] = inputValue;
        break;
        
      case 'file':
        // File handling is done separately
        break;
        
      case 'form':
        question.fields.forEach(field => {
          const input = questionEl.querySelector(`#${field.id}`);
          if (input) {
            this.answers[field.id.replace('-', '_')] = input.value;
          }
        });
        break;
    }
  }

  checkDisqualification(question) {
    if (!question.options) return false;
    
    const answer = this.answers[question.id];
    const option = question.options.find(opt => opt.value === answer);
    
    return option?.disqualifies === true;
  }

  showDisqualificationMessage() {
    const message = this.config.disqualificationMessage || {
      title: "Unfortunately, we've determined that our service isn't the best option for you right now.",
      content: "Thank you for your interest. Please contact us if you have any questions.",
      contactInfo: {
        phone: "1-(888)-231-7725",
        email: "team@newsmilelife.com"
      }
    };
    
    // Hide navigation
    document.getElementById('quiz-navigation')?.classList.add('hidden');
    document.getElementById('quiz-back-btn')?.classList.add('hidden');
    
    // Show message
    this.form.innerHTML = `
      <div class="quiz-disqualification">
        <h1>${message.title}</h1>
        <p>${message.content}</p>
        ${message.contactInfo ? `
          <div class="quiz-contact-info">
            <p><strong>Call or Text:</strong> <a href="tel:${message.contactInfo.phone.replace(/\D/g, '')}">${message.contactInfo.phone}</a></p>
            <p><strong>Email:</strong> <a href="mailto:${message.contactInfo.email}">${message.contactInfo.email}</a></p>
          </div>
        ` : ''}
      </div>
    `;
    
    document.body.classList.add('quiz-completed', 'quiz-disqualified');
  }

  showExitConfirmation() {
    const overlay = document.createElement('div');
    console.log("running exit confirmation")
    overlay.className = 'quiz-overlay';
    overlay.innerHTML = `
      <div class="quiz-modal">
        <h2>Are you sure you want to exit?</h2>
        <p>Your progress will be lost.</p>
        <div class="quiz-modal-buttons">
          <button class="quiz-modal-btn quiz-modal-cancel">Continue Quiz</button>
          <button class="quiz-modal-btn quiz-modal-confirm">Exit</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(overlay);
    
    overlay.querySelector('.quiz-modal-cancel').addEventListener('click', () => {
      overlay.remove();
    });
    
    overlay.querySelector('.quiz-modal-confirm').addEventListener('click', () => {
      window.location.href = '/';
    });
  }

  async handleFileUpload(input) {
    if (!input.files || !input.files[0]) return;
    
    const file = input.files[0];
    const question = this.config.questions[this.currentQuestionIndex];
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = document.getElementById(`${question.id}-preview`);
      if (preview) {
        preview.innerHTML = `<img src="${e.target.result}" alt="Preview">`;
        preview.classList.remove('hidden');
      }
    };
    reader.readAsDataURL(file);
    
    // Upload file
    if (question.uploadConfig) {
      this.imageUploading = true;
      
      try {
        let uploadUrl;
        
        if (question.uploadConfig.provider === 'imgur') {
          uploadUrl = await this.uploadToImgur(file, question.uploadConfig.clientId);
        } else if (question.uploadConfig.customUpload) {
          uploadUrl = await question.uploadConfig.customUpload(file);
        }
        
        this.answers[`${question.id}_url`] = uploadUrl;
        this.imageUploading = false;
        
      } catch (error) {
        console.error('Upload failed:', error);
        this.imageUploading = false;
      }
    }
  }

  async uploadToImgur(file, clientId) {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch('https://api.imgur.com/3/image', {
      method: 'POST',
      headers: {
        'Authorization': `Client-ID ${clientId}`
      },
      body: formData
    });
    
    const data = await response.json();
    if (data.success) {
      return data.data.link;
    } else {
      throw new Error('Upload failed');
    }
  }

async submitQuiz() {
    // Validate last question if it's a form
    const lastQuestion = this.config.questions[this.currentQuestionIndex];
    if (lastQuestion.type === 'form' && !this.validateCurrentQuestion()) {
      return;
    }
    
    // Save last answer
    this.saveCurrentAnswer();
    
    // Show loading
    this.showLoading();
    
    try {
      // Prepare submission data
      const submissionData = {
        ...this.answers,
        timestamp: new Date().toISOString(),
        quizId: this.config.id,
        date: new Date().toLocaleString()
      };
      
      // Add any additional data from config
      if (this.config.additionalData) {
        for (let key in this.config.additionalData) {
          if (typeof this.config.additionalData[key] === 'function') {
            submissionData[key] = this.config.additionalData[key]();
          } else {
            submissionData[key] = this.config.additionalData[key];
          }
        }
      }
      
      // Sending as form-encoded for Zapier webhooks instead of JSON
      // Wrap the data in a 'data' field as JSON string (Zapier expects this format)
      const formData = new URLSearchParams();
      formData.append('data', JSON.stringify(submissionData));
      
      // Submit to webhook
      const response = await fetch(this.config.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });
      
      // Check response
      let responseData;
      try {
        const responseText = await response.text();
        responseData = JSON.parse(responseText);
      } catch (e) {
        // If response isn't JSON, that's okay for Zapier
        responseData = { status: response.ok ? 'success' : 'error' };
      }
      
      if (response.ok || responseData.status === 'success' || this.config.ignoreWebhookResponse) {
        // Check for disqualification based on answers
        if (this.checkFinalDisqualification()) {
          this.showDisqualificationMessage();
        } else {
          // Success
          if (this.config.onSuccess) {
            this.config.onSuccess(submissionData);
          } else if (this.config.successRedirect) {
            setTimeout(() => {
              window.location.href = this.config.successRedirect;
            }, 1000);
          } else {
            this.showSuccessMessage();
          }
        }
      } else {
        throw new Error('Submission failed');
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      this.hideLoading();
      this.showErrorMessage();
    }
  }
  
  // Method to check for disqualification at submission
  checkFinalDisqualification() {
    // Check if any disqualifying answers were given
    for (let questionId in this.answers) {
      const question = this.config.questions.find(q => q.id === questionId);
      if (question && question.options) {
        const answer = this.answers[questionId];
        const option = question.options.find(opt => opt.value === answer);
        if (option && option.disqualifies) {
          return true;
        }
      }
    }
    return false;
  }

  showLoading() {
    const overlay = document.createElement('div');
    overlay.className = 'quiz-loading-overlay';
    overlay.innerHTML = `
      <div class="quiz-loading">
        <div class="quiz-spinner"></div>
        <p>Submitting your responses...</p>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  hideLoading() {
    const overlay = document.querySelector('.quiz-loading-overlay');
    if (overlay) overlay.remove();
  }

  showSuccessMessage() {
    this.hideLoading();
    
    const message = this.config.successMessage || {
      title: "Thank you!",
      content: "Your submission has been received. We'll be in touch soon."
    };
    
    // Hide navigation
    document.getElementById('quiz-navigation')?.classList.add('hidden');
    document.getElementById('quiz-back-btn')?.classList.add('hidden');
    
    this.form.innerHTML = `
      <div class="quiz-success">
        <h1>${message.title}</h1>
        <p>${message.content}</p>
        ${message.buttonText ? `
          <a href="${message.buttonUrl || '/'}" class="quiz-success-button">
            ${message.buttonText}
          </a>
        ` : ''}
      </div>
    `;
    
    document.body.classList.add('quiz-completed');
  }

  showErrorMessage() {
    const message = this.config.errorMessage || {
      title: "Something went wrong",
      content: "Please try again or contact support if the problem persists."
    };
    
    alert(`${message.title}\n\n${message.content}`);
  }

  // Utility method to get cookie value
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  // Utility method to set cookie
  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  }

  // Save progress to localStorage (optional feature)
  saveProgress() {
    if (this.config.navigation?.saveProgress) {
      const progressData = {
        quizId: this.config.id,
        currentQuestionIndex: this.currentQuestionIndex,
        answers: this.answers,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(`quiz_progress_${this.config.id}`, JSON.stringify(progressData));
    }
  }

  // Load progress from localStorage
  loadProgress() {
    if (this.config.navigation?.saveProgress) {
      const saved = localStorage.getItem(`quiz_progress_${this.config.id}`);
      if (saved) {
        try {
          const progressData = JSON.parse(saved);
          // Check if progress is less than 24 hours old
          const savedTime = new Date(progressData.timestamp);
          const now = new Date();
          const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
          
          if (hoursDiff < 24) {
            this.currentQuestionIndex = progressData.currentQuestionIndex;
            this.answers = progressData.answers;
            return true;
          }
        } catch (e) {
          console.error('Failed to load progress:', e);
        }
      }
    }
    return false;
  }

  // Clear saved progress
  clearProgress() {
    if (this.config.navigation?.saveProgress) {
      localStorage.removeItem(`quiz_progress_${this.config.id}`);
    }
  }

  // Analytics tracking (optional)
  trackEvent(action, label, value) {
    if (window.gtag && this.config.enableAnalytics) {
      window.gtag('event', action, {
        event_category: 'Quiz',
        event_label: label,
        value: value
      });
    }
  }

  // Cleanup method
  destroy() {
    // Remove event listeners
    this.form.removeEventListener('change', this.handleChange);
    this.form.removeEventListener('click', this.handleClick);
    
    // Clear body classes
    document.body.classList.remove('quiz-active', 'quiz-completed');
    
    // Remove navigation
    const navigation = document.getElementById('quiz-navigation');
    if (navigation) navigation.remove();
    
    // Clear saved progress
    this.clearProgress();
  }
}

// Initialize quiz based on configuration
function initializeQuiz(configName) {
  const config = window.quizConfigs && window.quizConfigs[configName];
  
  if (!config) {
    console.error(`Quiz configuration '${configName}' not found`);
    return;
  }
  
  const formElement = document.getElementById('quiz-container') || document.getElementById('da-quiz');
  
  if (!formElement) {
    console.error('Quiz container element not found');
    return;
  }
  
  // Create quiz instance
  window.currentQuiz = new QuizEngine(config, formElement);
}

// Auto-initialize if data attribute is present
document.addEventListener('DOMContentLoaded', function() {
  const quizContainer = document.getElementById('quiz-container') || document.getElementById('da-quiz');
  if (quizContainer) {
    const configName = quizContainer.dataset.quizConfig;
    if (configName) {
      initializeQuiz(configName);
    }
  }
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { QuizEngine, initializeQuiz };
}